import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Article,
  DashboardCustomize,
  Group,
  Inventory2,
  Restaurant,
  Science,
} from '@mui/icons-material';

const overviewMetrics = [
  {
    label: 'Products / Formulations',
    value: '103 / 143',
    icon: <Restaurant sx={{ fontSize: 17 }} />,
  },
  {
    label: 'Users',
    value: '25',
    icon: <Group sx={{ fontSize: 17 }} />,
  },
  {
    label: 'Ingredients',
    value: '189',
    icon: <Inventory2 sx={{ fontSize: 17 }} />,
  },
  {
    label: 'Documents',
    value: '194',
    icon: <Article sx={{ fontSize: 17 }} />,
  },
];

const flmMetrics = [
  { label: 'Idea', value: '137' },
  { label: 'Prototype', value: '8' },
  { label: 'Scale-up', value: '6' },
  { label: 'Production', value: '8' },
];

function MetricCard({ metric }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        minHeight: 62,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.25,
              fontSize: 10.3,
              mb: 0.45,
            }}
          >
            {metric.label}
          </Typography>

          <Typography
            sx={{
              fontSize: 18,
              lineHeight: 1,
              fontWeight: 900,
              color: 'text.primary',
            }}
          >
            {metric.value}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {metric.icon}
        </Box>
      </Stack>
    </Paper>
  );
}

function StageCard({ stage }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        p: 0.90,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
        borderTop: '3px solid',
        borderTopColor: 'primary.main',
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          fontWeight: 800,
          textTransform: 'uppercase',
          fontSize: 10.2,
          mb: 0.45,
        }}
      >
        {stage.label}
      </Typography>

      <Typography
        sx={{
          fontSize: 17,
          lineHeight: 1,
          fontWeight: 900,
          color: 'text.primary',
        }}
      >
        {stage.value}
      </Typography>
    </Paper>
  );
}

export default function ManagementPlatformOverviewVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 270,
        p: 1.5,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.6,
            py: 1.15,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DashboardCustomize sx={{ fontSize: 17 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Admin dashboard
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25 }}
              >
                Platform-wide activity and FLM metrics
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Overview"
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1.1,
              fontWeight: 800,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Platform totals
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.85,
              mb: 1.2,
            }}
          >
            {overviewMetrics.map((metric) => (
              <MetricCard key={metric.label} metric={metric} />
            ))}
          </Box>

          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.1 }}>
            <Science sx={{ fontSize: 16, color: 'primary.main' }} />

            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Metrics for FLM
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            {flmMetrics.map((stage) => (
              <StageCard key={stage.label} stage={stage} />
            ))}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}